import { KeyboardEvent, MouseEvent, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Game } from '../api/types';
import { addFavorite, removeFavorite } from '../api/playerGames';
import { useAuth } from '../auth/AuthContext';
import { rememberCatalogPosition } from '../lib/gameLaunch';

interface Props {
  game: Game;
  favorited?: boolean;
  dense?: boolean;
  fundingSource?: 'real' | 'bonus';
}

export function GameCard({ game, favorited = false, dense = false, fundingSource }: Props) {
  const { t } = useTranslation('games');
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isFav, setIsFav] = useState(favorited);

  async function onToggleFavorite(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (!user) return;
    try {
      if (isFav) {
        await removeFavorite(game.id);
        setIsFav(false);
      } else {
        await addFavorite(game.id);
        setIsFav(true);
      }
    } catch {
      // keep prior state
    }
  }

  function openGame() {
    rememberCatalogPosition(`${location.pathname}${location.search}`);
    const funding = fundingSource === 'bonus' ? '?funding=bonus' : '';
    navigate(`/play/${encodeURIComponent(game.id)}${funding}`);
  }

  function onKeyOpen(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openGame();
    }
  }

  return (
    <article className={`game-card game-card-clean${dense ? ' game-card-dense' : ''}`}>
      <div
        className="game-card-link game-card-hit"
        role="button"
        tabIndex={0}
        aria-label={game.name}
        onClick={openGame}
        onKeyDown={onKeyOpen}
      >
        <div className="game-thumb">
          {game.thumbnail_url ? (
            <img src={game.thumbnail_url} alt="" loading="lazy" />
          ) : (
            <span className="game-thumb-fallback" aria-hidden="true">
              {game.name.slice(0, 1)}
            </span>
          )}
          <button
            type="button"
            className={`fav-heart${isFav ? ' active' : ''}`}
            aria-label={t('favorite', { defaultValue: 'Favorite' })}
            aria-pressed={isFav}
            onClick={(e) => void onToggleFavorite(e)}
            disabled={!user}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M12 21s-7-4.5-9.5-9A5.5 5.5 0 0112 6.5 5.5 5.5 0 0121.5 12C19 16.5 12 21 12 21z"
                fill={isFav ? 'currentColor' : 'none'}
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          </button>
          {!game.playable ? <span className="game-badge game-badge-corner">{t('notPlayable')}</span> : null}
        </div>
      </div>
    </article>
  );
}
